import { signIn } from "@/lib/cloudflare/auth-client";
import { buildGettingStartedPath } from "@/lib/cloudflare/billing-client";
import { useState } from "react";
import { usePostHog } from 'posthog-js/react';
import * as Sentry from '@sentry/react';

interface SignInButtonProps {
  variant?: "default" | "dark" | "secondary" | "white" | "standard";
  location?: string;
}

export function SignInButton({ variant = "standard", location = "unknown" }: SignInButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();

  const handleSignIn = () => {
    if (isLoading) return;
    setIsLoading(true);
    
    Sentry.metrics.count('button_click', 1, { attributes: { location, variant } });
    if (posthog) {
      posthog.capture('clicked_get_started', { location, variant });
    }
    
    signIn(buildGettingStartedPath());
  };

  const baseStyles =
    "relative inline-flex items-center justify-center gap-3 transition-all duration-300 transform active:scale-[0.98] group overflow-hidden";

  const sizeStyles = variant === "standard" || variant === "secondary" || variant === "white"
    ? "h-12 px-8 rounded-full text-base font-medium"
    : "h-14 px-10 rounded-2xl text-lg font-semibold animate-pulse-subtle";

  const variantStyles = {
    standard: "text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 shadow-sm",
    default: "text-white bg-blue-600 hover:bg-blue-700 shadow-[0_20px_50px_rgba(37,99,235,0.3)]",
    dark: "text-slate-900 bg-white hover:bg-slate-50 shadow-[0_20px_50px_rgba(255,255,255,0.2)]",
    secondary: "text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200",
    white: "text-blue-600 bg-white hover:bg-blue-50 shadow-lg"
  }[variant];

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        onClick={handleSignIn}
        disabled={isLoading}
        className={`${baseStyles} ${sizeStyles} ${variantStyles}`}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span className="relative z-10">
          {isLoading ? "Connecting..." : "Connect Gmail to get started"}
        </span>
      </button>
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] opacity-80">
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        Secure Google OAuth integration
      </div>
    </div>
  );
}

